/**
 * BookingFee — the flat charge an operator adds once per order on top of the
 * tickets, set on the workspace and overridable per event.
 *
 * No document lives at a booking-fee path. The shape nests on the two
 * documents that do — `Workspace.bookingFee` (the default every event
 * inherits) and `Event.bookingFee` (the override) — and this file is the one
 * home for that shape and for the two derivations every pricing site shares,
 * the same way `OrderLineSchema` is declared once for the two documents that
 * carry it.
 *
 * A Vorverkaufsgebühr the organiser sells alongside the admission is an
 * unselbständige Nebenleistung to that admission and takes the admission's own
 * VAT rate (BFH V R 16/09, 03.11.2011). That is why the fee carries no rate of
 * its own: `splitFeeByRate` reads every rate it charges at off the very lines
 * it is ancillary to, and a basket spanning two rates yields one fee line per
 * rate rather than one figure at a rate nothing supports.
 */
import { z } from "zod";
import type { Event } from "./event.entity.ts";
import {
	apportionByRate,
	type BookingFeeLine,
	type DiscountLine,
	type TicketLine,
} from "./order.entity.ts";
import { Cents } from "./ticket-type.entity.ts";
import type { Workspace } from "./workspace.entity.ts";

// Fields justified:
//   amount          — the flat charge itself; 0 IS the disabled state, so the
//                     shape never needs a second way to say "no fee" and
//                     `resolveBookingFee` is total
//   onPartialReturn — the one choice the retention posture leaves an operator
// Excluded:
//   vatRate — the fee takes the admission's rate, read off the ticket lines it
//     is ancillary to at split time; a stored copy would be free to contradict
//     the lines it claims to describe
//   a percentage or per-ticket basis — the charge is flat per order, and a
//     percentage of the payment also reads as a card surcharge (§ 270a BGB)
//   onFullReturn — a full return always releases the fee in full; a blanket
//     exclusion of that in the AGB is void (§ 307 BGB; LG München I
//     37 O 5667/20, confirmed BGH X ZR 1/21), so nothing here is configurable
export const BookingFeeSchema = z.object({
	amount: Cents.describe(
		"the flat cents charged once on an order that carries paid tickets — zero is the fee switched off, not a fee of nothing",
	),
	onPartialReturn: z
		.enum(["keep", "proRata"])
		.default("keep")
		.describe(
			"what becomes of the fee when SOME of an order's tickets are returned — kept whole, or given back in proportion to what went back. A FULL return always releases it in full and is not governed by this field",
		),
});
export type BookingFee = z.infer<typeof BookingFeeSchema>;
// onPartialReturn carries a .default(), so a writer that lets the schema fill
// it in types its own literal against this input shape, not the output above.
export type BookingFeeInput = z.input<typeof BookingFeeSchema>;

/**
 * The fee that governs one event — the event's own answer where it has one,
 * the workspace default otherwise. Whole-object override, the shape a ticket
 * type's `salesWindow` already uses: an event either states its fee or
 * inherits it, and there is no half-inherited fee whose amount comes from one
 * document and whose return policy comes from another.
 *
 * Total by construction — the workspace field carries a schema default, so
 * every workspace card has a fee (zero, where nobody set one) and this never
 * has to answer "no fee configured" as a third case. No I/O: a Cloud Function
 * pricing a settle and a browser view pricing a basket both call it.
 */
export function resolveBookingFee(
	workspace: Pick<Workspace, "bookingFee">,
	event: Pick<Event, "bookingFee">,
): BookingFee {
	return event.bookingFee ?? workspace.bookingFee;
}

/**
 * The fee, as the order lines that carry it — one per distinct VAT rate among
 * the lines it is ancillary to, each apportioned by that rate's share of what
 * the buyer actually pays at it.
 *
 * `lines` is the order's ticket AND discount lines, never a booking-fee line: the
 * discriminant in the parameter type is what makes feeding one back in a
 * compile error rather than a fee charged on a fee. Passing the discount
 * lines is not optional bookkeeping — a Rabatt reduces the Entgelt of the
 * supplies it relates to (§ 10 Abs. 1 UStG), so the consideration the fee is
 * ancillary to at a given rate is that rate's ticket amounts net of the
 * discount taken off them, and apportioning by the undiscounted figure would
 * charge the fee against money nobody pays.
 *
 * The apportionment itself is `apportionByRate` (order.entity.ts), which a
 * voucher's discount lines are built from too — one rounding rule, so a basket
 * carrying both a fee and a discount cannot round them a cent apart on the
 * invoice that prints them together.
 *
 * Returns no lines at all when the lines it is handed are worth nothing net.
 * That is what keeps a free event free, and it is load-bearing well past the
 * arithmetic: a fee on a €0 basket makes the order non-zero, which mints a
 * § 14 invoice number for an event that supplied nothing for consideration.
 * This guard is on the net across every rate, which is the basket-level fact;
 * `apportionByRate`'s own is on the rates that carry consideration, and a
 * mixed-sign basket is the case where the two differ.
 *
 * No I/O, like `perTicketShares` and `vatCents`: the same three sites that
 * already agree on a price call it.
 */
export function splitFeeByRate(
	feeAmount: number,
	lines: readonly Pick<
		TicketLine | DiscountLine,
		"kind" | "vatRate" | "amount"
	>[],
): BookingFeeLine[] {
	let net = 0;
	for (const line of lines) net += line.amount;
	if (net <= 0) return [];
	return apportionByRate(feeAmount, lines).map(({ vatRate, cents }) => ({
		kind: "bookingFee",
		vatRate,
		amount: cents,
	}));
}
